import { ca } from "date-fns/locale";
import { diseaseAPI } from ".";
import { clinicCorporate, dxaiQuestions, prodClient } from "../utils/constants";
import { options } from "../utils/diseasesList";
import {
  ClinicCreateUser,
  ClinincInitialPage,
  Prescription,
  Recommendations,
  Score,
  UploadReport,
  UserDetails,
  VikritiRecommendations,
} from "../utils/interfaces";
import axios from "axios";
import { log } from "node:console";
import { epochToIST, epochToISTDate } from "../utils/eira/helper";

export const getFormatttedPrakritiQuestions = async (email: string) => {
  try {
    const response = await diseaseAPI.getScoreQuestions(email, "Prakriti");
    if (response && response.length > 0) {
      let formattedQuestions: any[] = [];

      // Loop through the response and format each question
      response.forEach((prakritiRes: any) => {
        const prakritFields = {
          dependent_question_action:
            prakritiRes.attributes.dependent_question_action,
          dependent_question_id: prakritiRes.attributes.dependent_question_id,
          dependent_question_value:
            prakritiRes.attributes.dependent_question_value,
          is_sub_question: prakritiRes.attributes.is_sub_question,
          question_subcontent:
            prakritiRes.attributes.question_description ?? "",
          dosha: prakritiRes.attributes.dosha,
          sub_points: prakritiRes.attributes.sub_points,
          question_id: prakritiRes.attributes.question_id,
          question_type: prakritiRes.attributes.option_type,
          question_text: prakritiRes.attributes.question_text,
          question_values: prakritiRes.attributes.question_values,
          score_name: prakritiRes.attributes.score_name,
          question_description:
            prakritiRes.attributes.question_description ?? null,
        };

        // Push each question as an individual array inside the formattedQuestions array
        formattedQuestions.push(prakritFields);
      });

      // Return formatted questions
      return {
        questions: formattedQuestions,
      };
    } else {
      throw new Error("No Prakriti questions found");
    }
  } catch (err) {
    console.error("Error:", err);
    throw err;
  }
};

export const getFormatttedPrakritiQuestionsPatientClinic = async (
  email: string
) => {
  try {
    const response = await diseaseAPI.getScoreQuestions(email, "Prakriti");
    if (response && response.length > 0) {
      let formattedQuestions: any[] = [];

      // Loop through the response and format each question
      response.forEach((prakritiRes: any) => {
        const prakritFields = {
          dependent_question_action:
            prakritiRes.attributes.dependent_question_action,
          dependent_question_id: prakritiRes.attributes.dependent_question_id,
          dependent_question_value:
            prakritiRes.attributes.dependent_question_value,
          is_sub_question: prakritiRes.attributes.is_sub_question,
          question_subcontent:
            prakritiRes.attributes.question_description ?? "",
          dosha: prakritiRes.attributes.dosha,
          sub_points: prakritiRes.attributes.sub_points,
          question_id: prakritiRes.attributes.question_id,
          question_type: prakritiRes.attributes.option_type,
          question_text: prakritiRes.attributes.question_text,
          question_values: prakritiRes.attributes.question_values,
          score_name: prakritiRes.attributes.score_name,
          question_description:
            prakritiRes.attributes.question_description ?? null,
        };

        // Push each question as an individual array inside the formattedQuestions array
        formattedQuestions.push(prakritFields);
      });

      // Sort the questions based on the numeric part of the question_id
      formattedQuestions.sort((a, b) => {
        const numA = parseInt(a.question_id.replace("PR_", ""));
        const numB = parseInt(b.question_id.replace("PR_", ""));
        return numA - numB;
      });

      // Return formatted questions
      return {
        questions: formattedQuestions,
      };
    } else {
      throw new Error("No Prakriti questions found");
    }
  } catch (err) {
    console.error("Error:", err);
    throw err;
  }
};

export const getScoreQuestions = async (userId: string, scoreType: string) => {
  const response = await diseaseAPI.getScoreQuestions(userId, scoreType);
  if (response && response.length > 0) {
    let formattedQuestions: any[] = [];

    // Loop through the response and format each question
    response.forEach((scoreRes: any) => {
      const scoreFields = {
        question_id: scoreRes.attributes.question_id,
        question_text: scoreRes.attributes.question_text ?? "",
        question_description: scoreRes.attributes.question_description ?? "",
        option_type: scoreRes.attributes.option_type,
        is_sub_question: scoreRes.attributes.is_sub_question,
        question_subcontent: scoreRes.attributes.question_subcontent ?? "",
        question_values: scoreRes.attributes.question_values,
        score_name: scoreRes.attributes.score_name,
        dependent_question_id: scoreRes.attributes.dependent_question_id,
        dependent_question_value: scoreRes.attributes.dependent_question_value,
        dependent_question_action:
          scoreRes.attributes.dependent_question_action,
      };

      // Push each question as an individual array inside the formattedQuestions array
      formattedQuestions.push(scoreFields);
    });
    // Return formatted questions
    return {
      questions: formattedQuestions,
    };
  }
};

export const getFormattedDxaiClinicQuestions = async (email: string) => {
  try {
    // Fetch DxAi_Clinic and Metabolic Score questions
    const [clinicResponse, medicalResponse] = await Promise.all([
      diseaseAPI.getScoreQuestions(email, "DxAi_Clinic"),
      diseaseAPI.getScoreQuestions(email, "Metabolic Score"),
    ]);

    // Initialize formatted questions storage
    const formattedQuestions: Record<string, any> = {};

    // Helper to process and filter questions
    const processQuestions = (questions: any[]) => {
      questions.forEach((question: any) => {
        const questionKey = question.attributes.question_id;
        if (clinicCorporate.includes(questionKey)) {
          formattedQuestions[questionKey] = {
            question_id: question.attributes.question_id,
            question_type: question.attributes.option_type,
            question_text: question.attributes.question_text,
            question_values: question.attributes.question_values,
          };
        }
      });
    };

    // Process both sets of questions
    if (clinicResponse?.length > 0) processQuestions(clinicResponse);
    if (medicalResponse?.length > 0) processQuestions(medicalResponse);

    // Log any questions from `dxaiQuestions` that are missing in the response
    const missingQuestions = dxaiQuestions.filter(
      (key) => !(key in formattedQuestions)
    );
    if (missingQuestions.length > 0) {
      console.warn(
        "The following questions are defined in dxaiQuestions but not present in the API response:",
        missingQuestions
      );
    }

    return { questions: formattedQuestions };
  } catch (err) {
    console.error("Error fetching questions:", err);
    throw err;
  }
};
const reformattedQuestions = (
  questionValue: number[],
  questionId: string,
  user_id: string
) => {
  // Map question IDs to the correct IDs
  const questionIdMap: { [key: string]: string } = {
    "Family History": "MS_1",
    "Medical History2": "MS_3",
    Symptoms: "MS_3",
    "Chief Complaints": "DxAi_11",
  };

  const question_id = questionIdMap[questionId] || questionId;

  // Generate the response object based on the questionValue array
  let reqData: any = {
    value: questionValue.includes(-1) ? -1 : questionValue.join(","),
  };

  return {
    questionnaire_id: question_id,
    response: JSON.stringify(reqData),
    user_id: user_id,
  };
};

export const getFormattedFileObject = async (
  file: File,
  user_id: string | null,
  client: string | null
): Promise<UploadReport | null> => {
  try {
    if (!file) {
      throw new Error("File is required to create a formatted file object.");
    }

    const allowedTypes = ["application/pdf", "image/png", "image/jpeg"];
    if (!allowedTypes.includes(file.type)) {
      console.error("Unsupported file type:", file.type);
      throw new Error(`Unsupported file type: ${file.type}`);
    }

    const reader = new FileReader();

    const base64Blob = await new Promise<string>((resolve, reject) => {
      reader.onloadend = () => {
        if (reader.result) {
          resolve(reader.result as string);
        } else {
          reject(new Error("Failed to convert file to Base64"));
        }
      };
      reader.onerror = () => reject(new Error("FileReader error"));
      reader.readAsDataURL(file);
    });

    const base64Content = base64Blob.split(",")[1];

    return {
      user_id,
      client,
      image_name: file.name,
      image_type: file.type,
      image_blob: base64Content,
    };
  } catch (error) {
    console.error("Error in getFormattedFileObject:", error);
    return null;
  }
};

export const saveClinicUserDetails = async (
  data: ClinincInitialPage,
  user_id: string,
  client: string
) => {
  try {
    const { Upload, "Basic Details": basicDetails } = data;
    const { Report, Tongue, Prescription } = Upload || {};

    const formattedApiKeys = ["MS_1", "MS_3"];

    // 1. Collecting reformatted API calls for parallel execution
    const reformattedRequests = formattedApiKeys
      .map((questionId) => {
        const questionData = data[questionId as keyof ClinincInitialPage] as
          | number[]
          | null;
        if (!questionData || questionData.length === 0) return null; // Skip empty questions
        const reformattedData = reformattedQuestions(
          questionData,
          questionId,
          user_id!
        );
        return diseaseAPI.saveScore(reformattedData);
      })
      .filter(Boolean); // Remove null values

    // 2. Collect all API calls for document uploads, ensuring non-empty arrays
    const documentUploads = [
      ...(Report && Report.length > 0
        ? Report.map((doc) => diseaseAPI.uploadDocuments(doc))
        : []),
      ...(Tongue && Tongue.length > 0
        ? Tongue.map((doc) => diseaseAPI.uploadDocuments(doc))
        : []),
      ...(Prescription && Prescription.length > 0
        ? Prescription.map((doc) => diseaseAPI.uploadDocuments(doc))
        : []),
    ];

    const height =
      data["Basic Vitals"]?.height_in_cms !== null &&
      typeof data["Basic Vitals"]?.height_in_cms === "string"
        ? parseFloat(data["Basic Vitals"].height_in_cms)
        : null;
    const weight =
      data["Basic Vitals"]?.weight_in_kg !== null &&
      typeof data["Basic Vitals"]?.weight_in_kg === "string"
        ? parseFloat(data["Basic Vitals"].weight_in_kg)
        : null;
    const waist =
      data["Basic Vitals"]?.waist_circumference_in_cms !== null &&
      typeof data["Basic Vitals"]?.waist_circumference_in_cms === "string"
        ? parseFloat(data["Basic Vitals"].waist_circumference_in_cms)
        : null;

    // 3. Preparing user details
    const userDetails = {
      client: prodClient, // client || null,
      user_id: user_id || null,
      age: basicDetails?.age || null,
      name: basicDetails?.name || null,
      gender: basicDetails?.gender || null,
      marital_status: basicDetails?.marital_status || null,
      current_address: basicDetails?.current_address || null,
      alternate_phn_number: basicDetails?.alternate_phn_number || null,
      phone_number: data.userDetail?.phone_number || null,
      email: data.userDetail?.email || null,
      diseases:
        data["Medical History1"]?.map((index) => options[index]) || null,
      height_in_cms: height,
      weight_in_kg: weight,
      waist_circumference_in_cms: waist,
      place_of_birth: data["Birth Details"]?.place || null,
      time_of_birth:
        data["Birth Details"]?.time_of_birth ||
        data["Birth Details"]?.date ||
        null,
    };

    // 4. Making all API calls in parallel
    await Promise.all([
      ...reformattedRequests,
      diseaseAPI.saveInitialPageUserDetails(userDetails),
      ...documentUploads,
    ]);
  } catch (err) {
    console.error("Error-Save-Clinic-User-Details:", err);
    throw err;
  }
};

export const getVikritiAnalysis = async (email: string) => {
  try {
    const response = await diseaseAPI.getVikritiAnalysis(
      `${email}@madmachines.in`
    );
    if (!response) {
      throw new Error("No Vikriti analysis found");
    }
    return response;
  } catch (err) {
    console.error("Error in getVikritiAnalysis:", err);
    throw new Error("Failed to fetch Vikriti analysis. Please try again.");
  }
};

const formatVikrutiType = (
  type: string,
  isScoreLabel: boolean = false
): string => {
  if (!isScoreLabel) {
    return type
      .split("_") // Split by underscore
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize
      .join(" "); // Join with a space
  } else {
    return type.split("_")[0].toUpperCase();
  }
};

export const getVikritiRecommendations = async (
  email: string
): Promise<Score> => {
  try {
    const response = await diseaseAPI.getVikritiRecommendation(
      `${email}@madmachines.in`
    );

    if (response) {
      const vikritiRecommendations: VikritiRecommendations = {
        all_unique_labels:
          response.all_unique_labels &&
          response.all_unique_labels !== "No recommendations"
            ? response.all_unique_labels
            : null,
        vikruti_type:
          response.vikruti_type &&
          response.vikruti_type !== "No recommendations"
            ? response.vikruti_type
            : null,
        prakriti_type:
          response.prakriti_type &&
          response.prakriti_type !== "No recommendations"
            ? response.prakriti_type
            : null,
        recommendation_type:
          response.recommendation_type &&
          response.recommendation_type !== "No recommendations"
            ? response.recommendation_type
            : null,
        recommendations:
          response.recommendations &&
          response.recommendations.length > 0 &&
          response.recommendations !== "No recommendations"
            ? response.recommendations
            : null,
      };

      return {
        label:
          response.vikruti_type &&
          response.vikruti_type !== "No recommendations"
            ? formatVikrutiType(response.vikruti_type)
            : "",
        max_score: 0, // Set defaults for irrelevant fields
        questions: [],
        score: 0,
        recommendations: {} as Recommendations,
        question_responses: [],
        score_label:
          response.vikruti_type &&
          response.vikruti_type !== "No recommendations"
            ? formatVikrutiType(response.vikruti_type, false)
            : "",
        vikrititRecomm: vikritiRecommendations,
      };
    } else {
      throw new Error("No Vikriti recommendations found");
    }
  } catch (err) {
    console.error("Error in getVikritiRecommendations:", err);
    throw new Error("Error in getVikritiRecommendations");
  }
};

export const downloadPdfwithUrl = async (email: string) => {
  // const pdfUrl = `https://d3ggzbwb0yiai7.cloudfront.net/madmachines/${email}.pdf`; // Replace with your PDF URL
  const pdfUrl = `https://d3ggzbwb0yiai7.cloudfront.net/madmachines/${email}.pdf`; // Replace with your PDF URL
  const filename = `${email.length > 0 ? email : "lab"}_reports.pdf`; // Desired file name

  axios
    .get(pdfUrl, {
      responseType: "blob",
    })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      // Clean up and remove the link
      link?.parentNode?.removeChild(link);
    })
    .catch((error) => {
      console.error("There was an error downloading the file:", error);
      throw error;
    });
};

export const fetchRecommendedScores = async (user_id: string) => {
  try {
    const response = ["Physical Activity Score"]; // Example response
    return response && response.length > 0
      ? { data: response, error: null }
      : { data: [], error: "No recommended scores returned from API" };
  } catch (error) {
    console.error("Error fetching recommended scores:", error);
    return { data: [], error };
  }
};

// Fetch the score Recommendations and then process the questions in each score
export const getScoreRecommendations = async (userId: string) => {};

export const saveScore = async (data: any) => {
  try {
    // Reformat the data as required
    const formattedData = {
      questionnaire_id: data.question_id, // Correcting the typo
      response: JSON.stringify({
        value: data.question_response.includes(-1)
          ? -1
          : data.question_response.map(String).join(","),
      }), // Convert array to a string inside JSON
      user_id: data.user_id, // Ensure user_id is a string
    };

    // Call the API to save the score
    await diseaseAPI.saveScore(formattedData);
  } catch (e) {
    console.error("Error in saveScore from apiCall:", e);
    throw e;
  }
};

// New function to fetch detailed doctors list for mapping

export const fetchDoctorsDetailMapList = async (): Promise<
  Map<number, any>
> => {
  try {
    const response = await diseaseAPI.getAllDcotors();

    // Check if response exists and has data with rows
    if (
      response &&
      response.data &&
      response.data.rows &&
      Array.isArray(response.data.rows) &&
      response.data.rows.length > 0
    ) {
      // Create a Map for O(1) lookup time
      const doctorsMap = new Map<number, any>();

      response.data.rows.forEach((doctor: any) => {
        const doctorDetails = {
          userId: doctor.userId,
          firstName: doctor.firstName,
          lastName: doctor.lastName,
          fullName: `${doctor.firstName} ${doctor.lastName}`,
          email: doctor.email,
          phone: doctor.phone,
          role: doctor.role,
          teamId: doctor.teamId,
          headDoctor: doctor.teamDetails?.headDoctor || false,
          teamName: doctor.teamDetails?.Team?.name || "",
          teamDesc: doctor.teamDetails?.Team?.desc || "",
          teamActive: doctor.teamDetails?.Team?.active || false,
          organizationId: doctor.teamDetails?.Team?.OrganizationId || null,
          departmentTypeId: doctor.teamDetails?.Team?.DepartmentTypeId || null,
          healthcareorgId: doctor.teamDetails?.Team?.HealthcareorgId || null,
          imageUrl: doctor.imageUrl || "",
        };

        doctorsMap.set(doctor.userId, doctorDetails);
      });

      return doctorsMap;
    }

    return new Map();
  } catch (error) {
    console.error("Error fetching doctors detail map:", error);
    return new Map();
  }
};

export const fetchAppointments = async (
  clinicUserId: string | null,
  fetchLatestOnly: boolean = false,
  doctorId?: string,
  needSorted: boolean = true, // New optional parameter for sorting control
  fetchLatestUpcoming: boolean = false // New parameter to fetch next upcoming appointment
): Promise<any[]> => {
  try {
    // Call the API to get all appointments for the user
    const [appointmentsResponse, doctorsMap] = await Promise.all([
      diseaseAPI.getAllAppointmentsBookedForUser(clinicUserId!),
      fetchDoctorsDetailMapList(),
    ]);

    // Check if response exists and has data array
    if (
      appointmentsResponse &&
      appointmentsResponse.data &&
      Array.isArray(appointmentsResponse.data) &&
      appointmentsResponse.data.length > 0
    ) {
      // Transform the response data into the expected format with doctor details
      let transformedAppointments = appointmentsResponse.data.map(
        (appointmentDetails: any) => {
          // Get doctor details from the map using UserId
          const doctorDetails = doctorsMap.get(appointmentDetails.UserId);

          // Use doctor details if available, otherwise fallback to appointment title
          const doctorName = doctorDetails
            ? doctorDetails.fullName
            : appointmentDetails.title;

          return {
            CaseId: appointmentDetails.CaseId,
            id: appointmentDetails.UserId,
            doctorName: doctorName,
            doctorDetails: doctorDetails || null, // Include full doctor details
            appointmentLink: appointmentDetails.appointmentLink,
            // Store raw timestamps for accurate filtering
            rawAppointmentDate: appointmentDetails.appointmentDate,
            rawStartTime: appointmentDetails.startTime,
            rawEndTime: appointmentDetails.endTime,
            appointmentType: appointmentDetails.appointmentType,
            consultationFee: appointmentDetails.consultationFee,
            consultationFeeOffline: appointmentDetails.consultationFeeOffline,
            appointmentDetails: {
              appointmentId: appointmentDetails.id,
              appointmentDate: epochToISTDate(
                appointmentDetails.appointmentDate
              ),
              startTime: epochToIST(appointmentDetails.startTime),
              endTime: epochToIST(appointmentDetails.endTime),
              // Keep raw timestamps for filtering
              appointmentDateRaw: appointmentDetails.appointmentDate,
              startTimeRaw: appointmentDetails.startTime,
              endTimeRaw: appointmentDetails.endTime,
              appointmentLink: appointmentDetails.appointmentLink,
              consultationFee: appointmentDetails.consultationFee,
              consultationFeeOffline: appointmentDetails.consultationFeeOffline,
            },
            patientId: appointmentDetails.PatientId, // Updated field name
            patientName: `${appointmentDetails.Patient.firstName} ${appointmentDetails.Patient.lastName}`,
            patientDetails: {
              patientId: appointmentDetails.Patient.patientId,
              patientMobileNo: appointmentDetails.Patient.mobile,
              patientGender: appointmentDetails.Patient.gender,
            },
            imageUrl: appointmentDetails.Patient.imageUrl,
          };
        }
      );

      // Filter by doctorId if fetchLatestOnly is true and doctorId is provided
      if (fetchLatestOnly && doctorId) {
        transformedAppointments = transformedAppointments.filter(
          (appointment: any) =>
            appointment.id.toString() === doctorId.toString()
        );

        if (transformedAppointments.length === 0) {
          console.log(
            `No appointments found for doctor ${doctorId} and patient ${clinicUserId}`
          );
          return [];
        }
      }

      // Handle fetchLatestOnly and needSorted combination
      if (fetchLatestOnly && !needSorted) {
        console.warn(
          "fetchLatestOnly requires sorting to determine 'latest'. Enabling sorting."
        );
      }

      // Handle fetchLatestUpcoming and needSorted combination
      if (fetchLatestUpcoming && !needSorted) {
        console.warn(
          "fetchLatestUpcoming requires sorting to determine next upcoming. Enabling sorting."
        );
      }

      // Sort if needSorted is true OR if fetchLatestOnly is true OR if fetchLatestUpcoming is true
      const shouldSort = needSorted || fetchLatestOnly || fetchLatestUpcoming;

      if (shouldSort) {
        // Sort appointments by start time (latest first)
        const sortedAppointments = transformedAppointments.sort(
          (a: any, b: any) => {
            // Primary sort: by appointment start time (latest first)
            const aStartTime = a.rawStartTime || 0;
            const bStartTime = b.rawStartTime || 0;

            // Convert to milliseconds if needed (epoch timestamps < year 2000 are likely in seconds)
            const aStartTimeMs =
              aStartTime < 1000000000000 ? aStartTime * 1000 : aStartTime;
            const bStartTimeMs =
              bStartTime < 1000000000000 ? bStartTime * 1000 : bStartTime;

            // Sort in descending order (latest first)
            if (bStartTimeMs !== aStartTimeMs) {
              return bStartTimeMs - aStartTimeMs;
            }

            // Secondary sort: by appointment date if start times are the same
            const aDate = a.rawAppointmentDate || 0;
            const bDate = b.rawAppointmentDate || 0;

            const aDateMs = aDate < 1000000000000 ? aDate * 1000 : aDate;
            const bDateMs = bDate < 1000000000000 ? bDate * 1000 : bDate;

            return bDateMs - aDateMs;
          }
        );

        // If fetchLatestUpcoming is true, find the next upcoming appointment
        if (fetchLatestUpcoming) {
          const now = Date.now();

          // Since sortedAppointments is sorted by latest first [8th, 6th, 1st...]
          // We need to iterate from the end to find the earliest upcoming appointment
          let nextUpcomingAppointment = null;

          for (let i = sortedAppointments.length - 1; i >= 0; i--) {
            const appointment = sortedAppointments[i];
            const endTime =
              appointment.appointmentDetails?.endTimeRaw ||
              appointment.rawEndTime ||
              0;
            const endTimeMs =
              endTime < 1000000000000 ? endTime * 1000 : endTime;

            if (endTimeMs > now) {
              nextUpcomingAppointment = appointment;
              break; // Found the earliest upcoming appointment
            }
          }

          if (nextUpcomingAppointment) {
            console.log(
              `Found next upcoming appointment for doctor ${doctorId}:`,
              nextUpcomingAppointment
            );
            return [nextUpcomingAppointment];
          } else {
            console.log(
              `No upcoming appointments found for doctor ${doctorId}. Returning all sorted appointments.`
            );
            return sortedAppointments;
          }
        }

        // If fetchLatestOnly is true, return only the first (latest) appointment
        if (fetchLatestOnly) {
          console.log(
            `Fetching latest appointment only for doctor ${doctorId}:`,
            sortedAppointments[0]
          );
          return sortedAppointments.length > 0 ? [sortedAppointments[0]] : [];
        }

        return sortedAppointments;
      } else {
        // Return unsorted appointments when needSorted is false and fetchLatestOnly is false
        console.log(
          `Returning unsorted appointments (${transformedAppointments.length} total)`
        );
        return transformedAppointments;
      }
    }

    // Return empty array if no valid data
    return [];
  } catch (error) {
    console.error("Error fetching appointments:", error);
    return [];
  }
};

export const fetchDoctorsDetailList = async (): Promise<any[]> => {
  try {
    // Call the API to get all doctors
    // Uncomment the line below when ready to use the actual API
    const response = await diseaseAPI.getAllDcotors();

    // Mock response for development
    // const response = {
    //   data: {
    //     rows: [
    //       {
    //         userId: 8,
    //         displayName: null,
    //         firstName: "Khushi",
    //         lastName: "M",
    //         enabled: true,
    //         email: "khushi@wemotiveforge.com",
    //         phone: "‪+912343454566‬",
    //         active: true,
    //         npi: "",
    //         team: "test",
    //         role: "Receptionist",
    //         isBanned: false,
    //         teamId: 2,
    //         teamDetails: {
    //           id: 2,
    //           headDoctor: false,
    //           createdAt: "2025-04-24T11:04:33.000Z",
    //           updatedAt: "2025-04-24T11:04:33.000Z",
    //           TeamId: 1,
    //           UserId: 8,
    //           Team: {
    //             id: 1,
    //             name: "Eye",
    //             desc: "Eye Desc",
    //             imageUrl: "",
    //             system: null,
    //             vendorId: null,
    //             active: true,
    //             createdAt: "2025-04-24T10:45:43.000Z",
    //             updatedAt: "2025-04-24T10:45:43.000Z",
    //             HealthcareorgId: null,
    //             OrganizationId: 4,
    //             DepartmentTypeId: 2,
    //           },
    //         },
    //         imageUrl: "",
    //       },
    //       {
    //         userId: 7,
    //         displayName: null,
    //         firstName: "gold",
    //         lastName: "man",
    //         enabled: true,
    //         email: "test@gmil.COM",
    //         phone: "‪+914987654323‬",
    //         active: true,
    //         npi: "",
    //         team: "test",
    //         role: "Doctor",
    //         isBanned: false,
    //         teamId: 1,
    //         teamDetails: {
    //           id: 1,
    //           headDoctor: false,
    //           createdAt: "2025-04-24T10:53:38.000Z",
    //           updatedAt: "2025-04-24T10:53:38.000Z",
    //           TeamId: 1,
    //           UserId: 7,
    //           Team: {
    //             id: 1,
    //             name: "Eye",
    //             desc: "Eye Desc",
    //             imageUrl: "",
    //             system: null,
    //             vendorId: null,
    //             active: true,
    //             createdAt: "2025-04-24T10:45:43.000Z",
    //             updatedAt: "2025-04-24T10:45:43.000Z",
    //             HealthcareorgId: null,
    //             OrganizationId: 4,
    //             DepartmentTypeId: 2,
    //           },
    //         },
    //         imageUrl: "",
    //       },
    //     ],
    //   },
    // };

    // Check if response exists and has data with rows
    if (
      response &&
      response.data &&
      response.data.rows &&
      Array.isArray(response.data.rows) &&
      response.data.rows.length > 0
    ) {
      // Transform the response data into the expected format
      const transformedDoctors = response.data.rows.map((doctor: any) => {
        return {
          id: doctor.userId,
          docId: doctor.userId,
          docName: `${doctor.firstName} ${doctor.lastName}`,
          name: `${doctor.firstName} ${doctor.lastName}`, // Adding name for compatibility
          email: doctor.email,
          phone: doctor.phone,
          role: doctor.role,
          specialisation: doctor.teamDetails?.Team?.name || "",
          TeamId: doctor.teamDetails?.TeamId || null,
          teamId: doctor.teamId,
          imageUrl: doctor.imageUrl || "",
          // Additional fields that might be needed by CustomDoctorCard
          department: doctor.teamDetails?.Team?.name || "",
          experience: "N/A", // Not available in the response
          rating: 4.5, // Default value since not in response
          consultationFee: 1000, // Default value since not in response
          about: `${doctor.firstName} ${doctor.lastName} is a ${
            doctor.role
          } specializing in ${
            doctor.teamDetails?.Team?.name || "general medicine"
          }.`,
          organizationId: doctor.teamDetails?.Team?.OrganizationId,
        };
      });

      return transformedDoctors;
    }

    // Return empty array if no valid data
    return [];
  } catch (error) {
    console.error("Error fetching doctors:", error);
    return [];
  }
};

export const getUserPrescription = async (
  userId: string
): Promise<Prescription[]> => {
  try {
    const response = await diseaseAPI.getUserPrescription(userId);
    return response;
    // return [
    //   {
    //     id: 2,
    //     nextFollowup: "2025-05-08T13:46:00.000Z",
    //     foodToAvoid: '["Spicy Food","Tamarind"]',
    //     foodToEat: '["Moong Dal","Wheat","Curd","Mango"]',
    //     createdAt: "2025-04-27T13:49:33.000Z",
    //     updatedAt: "2025-04-27T13:49:33.000Z",
    //     CaseId: 46,
    //     Medicines: [
    //       {
    //         id: 5,
    //         name: "Bharmi Grit",
    //         dosage: "1 tsp, 1-0-1",
    //         frequency: "7 days",
    //         instruction: "Please take with warm water",
    //         type: "agni",
    //         createdAt: "2025-04-27T13:49:33.000Z",
    //         updatedAt: "2025-04-27T13:49:33.000Z",
    //         PrescriptionId: 2,
    //       },
    //       {
    //         id: 6,
    //         name: "Triphala Churna",
    //         dosage: "1 tablet, 1-1-1",
    //         frequency: "7 days",
    //         instruction: "Please take with ghee",
    //         type: "dosha",
    //         createdAt: "2025-04-27T13:49:33.000Z",
    //         updatedAt: "2025-04-27T13:49:33.000Z",
    //         PrescriptionId: 2,
    //       },
    //       {
    //         id: 7,
    //         name: "Asarishtha",
    //         dosage: "1 tsbp, 1-0-0",
    //         frequency: "7 days",
    //         instruction: "Please take with ghee",
    //         type: "manovat",
    //         createdAt: "2025-04-27T13:49:33.000Z",
    //         updatedAt: "2025-04-27T13:49:33.000Z",
    //         PrescriptionId: 2,
    //       },
    //       {
    //         id: 8,
    //         name: "Smriti",
    //         dosage: "2 tablets, 1-0-1",
    //         frequency: "7 days",
    //         instruction:
    //           "Please take with ghee or jaggery or warm water or milk",
    //         type: "saundarya",
    //         createdAt: "2025-04-27T13:49:33.000Z",
    //         updatedAt: "2025-04-27T13:49:33.000Z",
    //         PrescriptionId: 2,
    //       },
    //     ],
    //   },
    //   {
    //     id: 1,
    //     nextFollowup: "2025-04-26T06:07:59.000Z",
    //     foodToAvoid: '["food","apple", "mango"]',
    //     foodToEat: '["food", "guava", "eemk]',
    //     createdAt: "2025-04-26T06:08:58.000Z",
    //     updatedAt: "2025-04-26T06:08:58.000Z",
    //     CaseId: 43,
    //     Medicines: [
    //       {
    //         id: 1,
    //         name: "Chandraprabha Vati",
    //         dosage: "1",
    //         frequency: "1",
    //         instruction: "1",
    //         type: "agni",
    //         createdAt: "2025-04-26T06:08:58.000Z",
    //         updatedAt: "2025-04-26T06:08:58.000Z",
    //         PrescriptionId: 1,
    //       },
    //       {
    //         id: 2,
    //         name: "Brahmi Vati",
    //         dosage: "1",
    //         frequency: "1",
    //         instruction: "1",
    //         type: "dosha",
    //         createdAt: "2025-04-26T06:08:58.000Z",
    //         updatedAt: "2025-04-26T06:08:58.000Z",
    //         PrescriptionId: 1,
    //       },
    //       {
    //         id: 3,
    //         name: "Brahmi Vati",
    //         dosage: "1",
    //         frequency: "1",
    //         instruction: "1",
    //         type: "manovat",
    //         createdAt: "2025-04-26T06:08:58.000Z",
    //         updatedAt: "2025-04-26T06:08:58.000Z",
    //         PrescriptionId: 1,
    //       },
    //       {
    //         id: 4,
    //         name: "Haridrakhanda Paka",
    //         dosage: "1",
    //         frequency: "1",
    //         instruction: "1",
    //         type: "saundarya",
    //         createdAt: "2025-04-26T06:08:58.000Z",
    //         updatedAt: "2025-04-26T06:08:58.000Z",
    //         PrescriptionId: 1,
    //       },
    //     ],
    //   },
    //   {
    //     id: 1,
    //     nextFollowup: "2025-03-26T06:07:59.000Z",
    //     foodToAvoid: '["food","apple", "mango"]',
    //     foodToEat: '["food", "guava", "eemk]',
    //     createdAt: "2025-02-26T06:08:58.000Z",
    //     updatedAt: "2025-02-26T06:08:58.000Z",
    //     CaseId: 43,
    //     Medicines: [
    //       {
    //         id: 1,
    //         name: "Chandraprabha Vati",
    //         dosage: "1",
    //         frequency: "1",
    //         instruction: "1",
    //         type: "agni",
    //         createdAt: "2025-04-26T06:08:58.000Z",
    //         updatedAt: "2025-04-26T06:08:58.000Z",
    //         PrescriptionId: 1,
    //       },
    //       {
    //         id: 2,
    //         name: "Brahmi Vati",
    //         dosage: "1",
    //         frequency: "1",
    //         instruction: "1",
    //         type: "dosha",
    //         createdAt: "2025-04-26T06:08:58.000Z",
    //         updatedAt: "2025-04-26T06:08:58.000Z",
    //         PrescriptionId: 1,
    //       },
    //       {
    //         id: 3,
    //         name: "Brahmi Vati",
    //         dosage: "1",
    //         frequency: "1",
    //         instruction: "1",
    //         type: "manovat",
    //         createdAt: "2025-04-26T06:08:58.000Z",
    //         updatedAt: "2025-04-26T06:08:58.000Z",
    //         PrescriptionId: 1,
    //       },
    //       {
    //         id: 4,
    //         name: "Haridrakhanda Paka",
    //         dosage: "1",
    //         frequency: "1",
    //         instruction: "1",
    //         type: "saundarya",
    //         createdAt: "2025-04-26T06:08:58.000Z",
    //         updatedAt: "2025-04-26T06:08:58.000Z",
    //         PrescriptionId: 1,
    //       },
    //     ],
    //   },
    // ];
  } catch (error) {
    console.error("Error fetching prescriptions:", error);
    return [];
  }
};
